import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { SearchField, Switch } from '../../../shared';
import { SettingsSection } from '../SettingsSection';
import { useTagNotificationPreferences } from './useTagNotificationPreferences';

import classes from './TagsSection.module.scss';

const TAGS_COLLAPSED_COUNT = 20;

export const TagsSection: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'pushNotifications' });

  const { tags, isLoadingTags, isTagEnabled, setTagEnabled } = useTagNotificationPreferences();

  const [searchQuery, setSearchQuery] = useState('');

  const [isExpanded, setIsExpanded] = useState(false);

  const matchedTags = useMemo(
    () => tags.filter(({ name }) => name.toLowerCase().includes(searchQuery.toLowerCase())),
    [searchQuery, tags]
  );

  const isSearchVisible = tags.length > TAGS_COLLAPSED_COUNT;

  const isCapped = !isExpanded && !searchQuery && matchedTags.length > TAGS_COLLAPSED_COUNT;

  const visibleTags = useMemo(() => {
    if (!isCapped) {
      return matchedTags;
    }

    return matchedTags.slice(0, TAGS_COLLAPSED_COUNT);
  }, [isCapped, matchedTags]);

  const rows = useMemo(() => {
    if (!matchedTags.length) {
      return <div className={classes['tags-section__empty']}>{t('common.nothing-found')}</div>;
    }

    return (
      <div className={classes['tags-section__rows']}>
        {visibleTags.map((tag) => {
          const { id, name } = tag;

          return (
            <Switch
              key={id}
              label={name}
              className={classes['tags-section__row']}
              checked={isTagEnabled(tag)}
              onChange={(isEnabled) => setTagEnabled({ tagId: id, isEnabled })}
            />
          );
        })}
      </div>
    );
  }, [isTagEnabled, matchedTags, setTagEnabled, t, visibleTags]);

  const showAllButton = useMemo(() => {
    if (!isCapped) {
      return null;
    }

    return (
      <button
        type={'button'}
        className={classes['tags-section__show-all']}
        onClick={() => setIsExpanded(true)}
      >
        {ctxT('tags-show-all', { total: tags.length })}
      </button>
    );
  }, [ctxT, isCapped, tags.length]);

  const search = useMemo(() => {
    if (!isSearchVisible) {
      return null;
    }

    return (
      <SearchField
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={t('common.type-to-search')}
        className={classes['tags-section__search']}
      />
    );
  }, [isSearchVisible, searchQuery, t]);

  const body = useMemo(() => {
    if (isLoadingTags) {
      return (
        <Skeleton
          containerClassName={classes['tags-section__loader']}
          height={'2.5rem'}
          count={5}
          inline
        />
      );
    }

    return (
      <>
        {search}
        {rows}
        {showAllButton}
      </>
    );
  }, [isLoadingTags, rows, search, showAllButton]);

  if (!isLoadingTags && !tags.length) {
    return null;
  }

  return (
    <SettingsSection title={ctxT('tags-title')} hint={ctxT('tags-hint')}>
      {body}
    </SettingsSection>
  );
};
